import { c as _c } from "react/compiler-runtime";
import type { EditorProps, Monaco } from "@monaco-editor/react";
import { Box, Skeleton, useColorScheme } from "@mui/material";
import type { editor } from "monaco-editor";
import dynamic from "next/dynamic";
import React, { useCallback, useRef } from "react";
import "#/features/codeRunner/lib/configureMonacoLoader";
import { useDeferredClientMount } from "#/shared/hooks/useDeferredClientMount";
import { useMobileLayout } from "#/shared/hooks/useMobileLayout";
const MonacoEditor = dynamic(() => import("@monaco-editor/react"), {
  ssr: false,
  loading: () => <Skeleton variant="rectangular" height="400px" />
});
type CodeRunnerProps = EditorProps & {
  isUpdating?: boolean;
  setMonacoInstance?: React.Dispatch<React.SetStateAction<Monaco | null>>;
  setEditorInstance?: React.Dispatch<React.SetStateAction<editor.IStandaloneCodeEditor | null>>;
  setTextModel: React.Dispatch<React.SetStateAction<editor.ITextModel | null>>;
  /** Clears parent-held Monaco refs when the shell unmounts or Activity hides the route. */
  onEditorUnmount?: () => void;
};
type MonacoEditorShellInnerProps = CodeRunnerProps;
const MonacoEditorShellInner: React.FC<MonacoEditorShellInnerProps> = t0 => {
  const $ = _c(25);
  let height;
  let onEditorUnmount;
  let restProps;
  let setEditorInstance;
  let setMonacoInstance;
  let setTextModel;
  if ($[0] !== t0) {
    ({
      onEditorUnmount,
      setMonacoInstance,
      setEditorInstance,
      setTextModel,
      height,
      ...restProps
    } = t0);
    $[0] = t0;
    $[1] = height;
    $[2] = onEditorUnmount;
    $[3] = restProps;
    $[4] = setEditorInstance;
    $[5] = setMonacoInstance;
    $[6] = setTextModel;
  } else {
    height = $[1];
    onEditorUnmount = $[2];
    restProps = $[3];
    setEditorInstance = $[4];
    setMonacoInstance = $[5];
    setTextModel = $[6];
  }
  const {
    mode
  } = useColorScheme();
  const isMobile = useMobileLayout();
  const editorRef = useRef(null);
  let t1;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      editorRef.current?.dispose();
      editorRef.current = null;
    };
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  const disposeEditor = t1;
  let t2;
  if ($[8] !== onEditorUnmount) {
    t2 = () => {
      disposeEditor();
      onEditorUnmount?.();
    };
    $[8] = onEditorUnmount;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const onShellCleanup = t2;
  const {
    isReady,
    mountKey
  } = useDeferredClientMount(onShellCleanup);
  const resolvedHeight = typeof height === "number" ? `calc(${height}px - 6vh)` : height;
  const t3 = height === "100%" ? "100%" : undefined;
  let t4;
  if ($[10] !== t3) {
    t4 = {
      position: "relative",
      height: t3
    };
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== isMobile || $[13] !== isReady || $[14] !== mode || $[15] !== mountKey || $[16] !== resolvedHeight || $[17] !== restProps || $[18] !== setEditorInstance || $[19] !== setMonacoInstance || $[20] !== setTextModel) {
    t5 = !isReady ? <Skeleton data-testid="playground-monaco-editor-loading" variant="rectangular" height={resolvedHeight ?? "400px"} sx={{
      width: "100%"
    }} /> : <Box data-testid="playground-monaco-editor" sx={{
      height: "100%"
    }}><MonacoEditor key={mountKey} theme={mode === "dark" ? "app-dark" : "vs-light"} options={{
        fontSize: 13,
        minimap: {
          enabled: false
        },
        tabSize: 2,
        fixedOverflowWidgets: true,
        lineNumbersMinChars: 3,
        lineDecorationsWidth: 6,
        folding: !isMobile,
        stickyScroll: {
          enabled: false
        },
        wordWrap: "on"
      }} {...restProps} height={resolvedHeight} onMount={(mountedEditor, monaco) => {
        const model = mountedEditor.getModel();
        if (!model) {
          console.error("No model found");
          return;
        }
        editorRef.current = mountedEditor;
        setEditorInstance?.(mountedEditor);
        setMonacoInstance?.(monaco);
        setTextModel(model);
        monaco.editor.defineTheme("app-dark", {
          base: "vs-dark",
          inherit: true,
          rules: [],
          colors: {
            "editor.background": "#00000000",
            focusBorder: "#00000000"
          }
        });
        monaco.editor.setTheme("app-dark");
      }} /></Box>;
    $[12] = isMobile;
    $[13] = isReady;
    $[14] = mode;
    $[15] = mountKey;
    $[16] = resolvedHeight;
    $[17] = restProps;
    $[18] = setEditorInstance;
    $[19] = setMonacoInstance;
    $[20] = setTextModel;
    $[21] = t5;
  } else {
    t5 = $[21];
  }
  let t6;
  if ($[22] !== t4 || $[23] !== t5) {
    t6 = <Box sx={t4}>{t5}</Box>;
    $[22] = t4;
    $[23] = t5;
    $[24] = t6;
  } else {
    t6 = $[24];
  }
  return t6;
};

/**
 * Monaco wrapper for the playground code panel.
 * Defers mount one frame, disposes on Activity hide/unmount, and clears parent refs.
 */
export const CodeRunner: React.FC<CodeRunnerProps> = props => {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <MonacoEditorShellInner {...props} />;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
};